/* heon-cart.js: owned by the cart team (2026-09-26). Loaded on every page by snippets/heon-head.liquid.
   Small additions on top of Beautify's own cart scripts, active only with the Heon drawer (cart-drawer.hc-drawer)
   and quick shop (quick-add-drawer.hc-qs):
   1. Quick shop: after "Add to cart" the quick shop gives way to the cart drawer, which shows the piece in the cart
      (the theme's default was a green "Item added" strip inside the quick shop).
   2. The line that was just added carries a small gold "Added" for a moment.
   3. The count on the bag icon answers an add with a short settle (skipped with reduced motion).
   4. "You may also like" in the drawer: three available pieces from Shopify's related products for the first piece in
      the cart, fetched when the drawer opens (cached per product). "Add +" opens the theme's quick shop. Their photos
      load straight away (they exist only once the drawer is open; lazy loading inside the drawer's scroller sometimes
      never started, leaving blank tiles).
   Round 2 (QA):
   5. The empty drawer's category photos get their src only when the drawer opens or the bag icon is hovered/touched.
   6. Quick shop option names read as on the product page ("Metal" then the chosen value, "Width" for a chain's size).
   7. Cart page: the chat bubble steps aside while the totals or Checkout pass under it, and emptying the cart brings
      in the bestsellers row the page shows when it loads empty. */
(() => {
  const T = (k, d) => { const s = window.HeonI18n && window.HeonI18n[k]; return typeof s === 'string' && s ? s : d; };   // the page's language (snippets/heon-i18n-js), else the English default
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let qsPending = 0;
  let qsOpener = null;

  const markNew = (drawer, key) => {
    if (!key) return;
    const line = drawer.querySelector(`.hc-line[data-key="${window.CSS && CSS.escape ? CSS.escape(key) : key}"]`);
    if (!line) return;
    line.classList.add('is-new');
    setTimeout(() => line.classList.remove('is-new'), 3700);
  };

  const bumpCount = () => {
    if (reduce()) return;
    const badge = document.querySelector('#cart-icon-bubble .count-badge');
    if (badge && badge.animate) {
      badge.animate([{ transform: 'scale(.55)', opacity: 0.3 }, { transform: 'scale(1.12)', opacity: 1, offset: 0.6 }, { transform: 'scale(1)' }],
        { duration: 480, easing: 'cubic-bezier(.22,.61,.36,1)' });
    }
  };

  // Esc with the note or discount panel open closes that panel, not the whole cart
  document.addEventListener('keyup', (evt) => {
    if (evt.key !== 'Escape') return;
    const openPanel = document.querySelector('.hc-drawer .hc-actions details[open]');
    const disclosure = openPanel && openPanel.closest('cart-summary-disclosure');
    if (disclosure && typeof disclosure.close === 'function') {
      evt.stopPropagation();
      disclosure.close();
    }
  }, true);

  customElements.whenDefined('quick-add-drawer').then(() => {
    const QA = customElements.get('quick-add-drawer');
    const original = QA.prototype.addedToCart;
    QA.prototype.addedToCart = function addedToCart() {
      if (this.classList.contains('hc-qs') && document.querySelector('cart-drawer.hc-drawer')) {
        qsPending = Date.now();
        qsOpener = this.opener || null;
        return undefined;
      }
      return original.call(this);
    };
  });

  customElements.whenDefined('cart-drawer').then(() => {
    const CD = customElements.get('cart-drawer');
    const original = CD.prototype.renderContents;
    CD.prototype.renderContents = async function renderContents(data, openDrawer = true, opener = null) {
      if (!this.classList.contains('hc-drawer')) return original.call(this, data, openDrawer, opener);
      let open = openDrawer;
      let from = opener;
      if (qsPending && Date.now() - qsPending < 10000) {
        open = true;
        from = qsOpener && qsOpener.isConnected ? qsOpener : opener;
        qsPending = 0;
        qsOpener = null;
      }
      const result = await original.call(this, data, open, from);
      if (data && data.key) markNew(this, data.key);
      return result;
    };
  });

  // ---------- recommendations in the drawer ----------
  const recCache = new Map();
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (cents) => {
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };
  const shortName = (title) => {
    let t = String(title || '');
    if (t.slice(0, 9).toLowerCase() === '14k gold ') t = t.slice(9);
    return t.split(', ')[0];
  };
  const sized = (src, w) => {
    if (!src) return '';
    const u = src.startsWith('//') ? `https:${src}` : src;
    return `${u}${u.includes('?') ? '&' : '?'}width=${w}`;
  };
  const getRecs = (productId) => {
    if (!recCache.has(productId)) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      recCache.set(productId, fetch(`${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=related`)
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((d) => (d && d.products) || [])
        .catch(() => { recCache.delete(productId); return []; }));
    }
    return recCache.get(productId);
  };
  const recTile = (p, quick) => {
    const name = shortName(p.title);
    const img = p.featured_image
      ? `<img src="${esc(sized(p.featured_image, 300))}" srcset="${esc(sized(p.featured_image, 200))} 200w, ${esc(sized(p.featured_image, 300))} 300w, ${esc(sized(p.featured_image, 450))} 450w" sizes="(min-width: 768px) 130px, 30vw" alt="" decoding="async" width="300" height="375">`
      : '';
    const price = `${p.price_varies ? T('from', 'From') + ' ' : ''}${money(p.price)}`;
    const add = quick
      ? `<button type="button" class="hc-rec__add js-quick-add" data-product-url="${esc(p.url)}" aria-label="${T('quick_add_aria', 'Add [name], quick shop').replace('[name]', () => esc(name))}"><span>${T('quick_add', 'Add +')}</span></button>`
      : '';
    return `<li class="hc-rec"><a class="hc-rec__link" href="${esc(p.url)}"><span class="hc-rec__media">${img}</span>`
      + `<span class="hc-rec__name">${esc(name)}</span><span class="hc-rec__price">${esc(price)}</span></a>${add}</li>`;
  };
  const fillRecs = (drawer) => {
    const box = drawer && drawer.querySelector('.hc-recs[data-product-id]');
    if (!box || box.dataset.filled || !box.dataset.productId) return;
    box.dataset.filled = 'pending';
    const inCart = new Set((box.dataset.inCart || '').split(',').filter(Boolean).map(Number));
    const quick = !!(customElements.get('quick-add-drawer') && document.querySelector('quick-add-drawer'));
    const render = (products) => {
      if (!box.isConnected) return;
      const picks = products.filter((p) => p && p.available && !inCart.has(Number(p.id))).slice(0, 3);
      box.dataset.filled = 'true';
      if (picks.length < 2) return;
      box.querySelector('.hc-recs__list').innerHTML = picks.map((p) => recTile(p, quick)).join('');
      box.hidden = false;
    };
    const pending = getRecs(box.dataset.productId);
    // answered before: fill right away so a quantity change does not make the row blink
    if (pending.hc) render(pending.hc);
    else pending.then((list) => { pending.hc = list; render(list); });
  };
  const loadThumbs = (root) => {
    if (!root) return;
    root.querySelectorAll('img[data-hc-src]').forEach((img) => {
      img.src = img.dataset.hcSrc;
      img.removeAttribute('data-hc-src');
    });
  };
  const watchDrawer = () => {
    const drawer = document.querySelector('cart-drawer.hc-drawer');
    if (!drawer) return;
    const isOpen = () => drawer.hasAttribute('open') || drawer.getAttribute('aria-hidden') === 'false';
    const onOpen = () => { loadThumbs(drawer); fillRecs(drawer); };
    if (isOpen()) onOpen();
    new MutationObserver(() => { if (isOpen()) onOpen(); })
      .observe(drawer, { attributes: true, attributeFilter: ['open', 'aria-hidden'], childList: true, subtree: true });
    // a hand on its way to the bag icon: start the empty-state photos a moment early
    const early = (evt) => { if (evt.target.closest && evt.target.closest('#cart-icon')) loadThumbs(drawer); };
    ['pointerover', 'touchstart', 'focusin'].forEach((t) => document.addEventListener(t, early, { passive: true }));
  };

  // ---------- quick shop option names, in the product page's words (sections/main-product.liquid) ----------
  const optLabel = (name, title) => {
    const k = String(name || '').toLowerCase();
    if (k.includes('color') || k.includes('colour') || k.includes('metal')) return T('opt_metal', 'Metal');
    if (k.includes('ring size')) return k.includes('us') ? T('opt_ring_size_us', 'Ring size (US)') : T('opt_ring_size', 'Ring size');
    if (k.includes('length')) return T('opt_length', 'Length');
    if (k.includes('width')) return T('opt_width', 'Width');
    if (k === 'size' && title.includes('chain')) return T('opt_width', 'Width');
    if (k.includes('size')) return T('opt_size', 'Size');
    if (k.includes('single')) return T('opt_single_pair', 'Single or pair');
    if (k.includes('gemstone')) return T('opt_stone', 'Stone');
    if (k.includes('shape')) return T('opt_style', 'Style');
    return name;
  };
  const chosen = (fs) => {
    const radio = fs.querySelector('input:checked');
    if (radio) return radio.value;
    const sel = fs.querySelector('select');
    return sel ? sel.value : '';
  };
  const relabel = (qs) => {
    const title = ((qs.querySelector('.product-title') || {}).textContent || '').toLowerCase();
    qs.querySelectorAll('fieldset.option-selector[data-option]').forEach((fs) => {
      const lg = fs.querySelector(':scope > legend');
      if (!lg) return;
      let k = lg.querySelector('.hp-legend__k');
      let v = lg.querySelector('.hp-legend__v');
      if (!k || !v) {
        lg.classList.add('hp-legend');
        lg.textContent = '';
        k = document.createElement('span');
        k.className = 'hp-legend__k';
        v = document.createElement('span');
        v.className = 'hp-legend__v';
        lg.append(k, ' ', v);
      }
      // theme i18n (2026-09-28): the English names are for English pages; a page in another language keeps the name the
      // product page printed from the locale (sections/main-product.liquid), else the option's own name
      const en = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase() === 'en';
      const label = en ? optLabel(fs.dataset.option, title) : (k.textContent || fs.dataset.option || '');
      const value = chosen(fs);
      if (k.textContent !== label) k.textContent = label;
      if (v.textContent !== value) v.textContent = value;
    });
  };
  const watchQuickShop = () => {
    const qs = document.querySelector('quick-add-drawer.hc-qs');
    if (!qs) return;
    let queued = false;
    const run = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; relabel(qs); });
    };
    new MutationObserver(run).observe(qs, { childList: true, subtree: true });
    qs.addEventListener('change', run);
  };

  // ---------- cart page ----------
  // the chat bubble (bottom right) steps aside while the summary (totals, note, discount, terms, Checkout) passes under it
  const watchChat = () => {
    if (!document.querySelector('.hc-page') || !('IntersectionObserver' in window)) return;
    const hits = new Set();
    let io = null;
    const start = () => {
      if (io) io.disconnect();
      hits.clear();
      const box = 88; // the bubble: 44-48px, 14-20px from the corner, plus air
      io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) hits.add(e.target); else hits.delete(e.target); });
        document.documentElement.classList.toggle('hc-chat-off', hits.size > 0);
      }, { rootMargin: `-${Math.max(0, window.innerHeight - box)}px 0px 0px -${Math.max(0, window.innerWidth - box)}px` });
      document.querySelectorAll('.hc-page :is(.hc-sum__rows, .hc-sum__fine, .cart-actions, .hc-terms, .hc-checkout)').forEach((el) => io.observe(el));
      if (!hits.size) document.documentElement.classList.remove('hc-chat-off');
    };
    start();
    let t = 0;
    const later = () => { clearTimeout(t); t = setTimeout(start, 250); };
    window.addEventListener('resize', later);
    document.addEventListener('on:line-item:change', later);
  };
  // the page was full and has just been emptied: fetch this section again for its bestsellers row
  document.addEventListener('on:line-item:change', (evt) => {
    const cart = evt.detail && evt.detail.cart;
    const page = document.querySelector('.hc-page');
    const items = page && page.querySelector('cart-items[data-section]');
    if (!cart || cart.item_count !== 0 || !items || page.querySelector('.hc-best')) return;
    fetch(`${window.location.pathname}?section_id=${encodeURIComponent(items.dataset.section)}`)
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const best = doc.querySelector('.hc-best');
        if (!best || page.querySelector('.hc-best')) return;
        doc.querySelectorAll('link[rel="stylesheet"][href]').forEach((l) => {
          const href = l.getAttribute('href');
          if (![...document.querySelectorAll('link[rel="stylesheet"]')].some((x) => x.getAttribute('href') === href)) {
            const n = document.createElement('link');
            n.rel = 'stylesheet';
            n.href = href;
            document.head.appendChild(n);
          }
        });
        doc.querySelectorAll('script[src]').forEach((sc) => {
          const src = sc.getAttribute('src');
          if (![...document.scripts].some((x) => x.getAttribute('src') === src)) {
            const n = document.createElement('script');
            n.src = src;
            n.defer = true;
            document.head.appendChild(n);
          }
        });
        page.appendChild(document.importNode(best, true));
      })
      .catch(() => {});
  });

  const boot = () => { watchDrawer(); watchQuickShop(); watchChat(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  customElements.whenDefined('product-form').then(() => {
    const PF = customElements.get('product-form');
    if (typeof PF.updateCartIcon !== 'function') return;
    const original = PF.updateCartIcon;
    PF.updateCartIcon = function updateCartIcon(response) {
      original.call(this, response);
      bumpCount();
    };
  });
})();
